import React from "react";
import { Modal } from "react-bootstrap";
import {
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Avatar,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";

import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
  ContactMail as ContactMailIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  AccountBalance as AccountBalanceIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { styled } from "@mui/material/styles";

const StatusChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.success.light
    : theme.palette.error.light,
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 600,
  fontSize: "0.875rem",
}));

const InfoRow = ({ icon, label, value, fullWidth = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      mb: 2,
      width: fullWidth ? "100%" : "auto",
    }}
  >
    <Box sx={{ mr: 2, color: "text.secondary", mt: 0.5 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
        {value || "Non renseigné"}
      </Typography>
    </Box>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: 600,
      mb: 2,
      color: "primary.main",
      display: "flex",
      alignItems: "center",
    }}
  >
    {children}
  </Typography>
);

const ProfilStructureModal = ({ show, handleClose, structure, onEdit }) => {
  if (!structure) return null;

  const {
    intitule,
    code,
    rccm,
    ifu,
    tel,
    adresse,
    email,
    emailValideDate,
    isActive,
    createdAt,
    responsable,
    typeStructure,
    secteurActivite,
  } = structure;

  const formatDate = (dateString) => {
    return dateString
      ? new Date(dateString).toLocaleDateString("fr-FR")
      : "Non renseigné";
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={handleClose}
      aria-labelledby="structure-modal-title"
      centered
      backdrop="static"
    >
      <Paper sx={{ p: 3 }}>
        <Modal.Header closeButton sx={{ borderBottom: "none", pb: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "success.main",
                }}
              >
                <BusinessIcon fontSize="inherit" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {intitule}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Code: {code || "Non renseigné"}
                </Typography>
              </Box>
            </Box>
            <Box>
              <StatusChip
                label={isActive ? "Active" : "Inactive"}
                active={isActive}
              />
              <Tooltip title="Modifier">
                <IconButton onClick={onEdit} sx={{ ml: 1 }}>
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Modal.Header>

        <Modal.Body style={{ overflowY: "auto", padding: "16px 0" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              mt: 2,
            }}
          >
            {/* Colonne de gauche - Photo et statut */}
            <Box
              sx={{
                width: { xs: "100%", md: "30%" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box sx={{ width: "100%", textAlign: "center" }}>
                {typeStructure && (
                  <Chip
                    label={typeStructure}
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                )}
                {secteurActivite && (
                  <Chip
                    label={secteurActivite}
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>

            {/* Colonne de droite - Détails */}
            <Box
              sx={{
                width: { xs: "100%", md: "70%" },
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {/* Section Informations de contact */}
              <Box>
                <SectionTitle>
                  <ContactMailIcon sx={{ mr: 1 }} color="success" />
                  <span className="text-success">Informations de contact</span>
                </SectionTitle>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <InfoRow icon={<EmailIcon />} label="Email" value={email} />
                    <InfoRow
                      icon={<PhoneIcon />}
                      label="Téléphone"
                      value={tel}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <InfoRow
                      icon={<HomeIcon />}
                      label="Adresse"
                      value={adresse}
                      fullWidth
                    />
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Section Informations légales */}
              <Box>
                <SectionTitle>
                  <AccountBalanceIcon sx={{ mr: 1 }} color="success" />
                  <span className="text-success "> Informations légales</span>
                </SectionTitle>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <InfoRow icon={<BadgeIcon />} label="IFU" value={ifu} />
                    <InfoRow
                      icon={<AssignmentIcon />}
                      label="RCCM"
                      value={rccm}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <InfoRow
                      icon={<CalendarTodayIcon />}
                      label="Date de création"
                      value={formatDate(createdAt)}
                    />
                    <InfoRow
                      icon={<CalendarTodayIcon />}
                      label="Email validé le"
                      value={formatDate(emailValideDate)}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Section Responsable */}
              {responsable && (
                <>
                  <Divider />
                  <Box>
                    <SectionTitle>
                      <PersonIcon sx={{ mr: 1 }} />
                      Responsable
                    </SectionTitle>
                    <InfoRow
                      icon={<PersonIcon />}
                      label="Nom"
                      value={responsable}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Modal.Body>

        <Modal.Footer sx={{ borderTop: "none", pt: 0, mt: 2 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: "100%", justifyContent: "space-between" }}
          >
            <Box>
              <Tooltip title="Imprimer">
                <IconButton>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Partager">
                <IconButton>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Button
              variant="contained"
              color="success"
              onClick={handleClose}
              sx={{ px: 4 }}
            >
              Fermer
            </Button>
          </Stack>
        </Modal.Footer>
      </Paper>
    </Modal>
  );
};

export default ProfilStructureModal;
